/**
 * Per-budget test/control ratio for the experiment report.
 *
 * Global arm totals are dominated by a few large budgets when audiences are calibrated
 * differently, so a metric can move at the portfolio level without any budget changing. This
 * compares the arms within each budget, then combines those ratios:
 *
 *     ratio_b   = metric(test, b) / metric(control, b)
 *     weight_b  = Remerge revenue of the control arm in b
 *     aggregate = exp( Σ_b weight_b · ln(ratio_b) / Σ_b weight_b )
 *
 * Weighting on the control arm alone keeps the weights identical across arms. Budgets are
 * dropped where the ratio is undefined or the control arm has no revenue. Above 1 means the
 * test arm is higher.
 *
 * Druid and ClickHouse are internal and plain HTTP, reachable only through the Vite dev-server
 * proxy, so callers must gate this on `import.meta.env.DEV`.
 */

import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import { runClickHouseQuery } from "../clickhouse/ClickHouseClient";
import { runDruidQuery } from "../druid/DruidClient";
import type { MetricKey } from "./ExperimentReport";
import type { Flight } from "./experimentFlights";

/**
 * Metrics the ratio is defined for. The reengagement log-losses need a per-bid weighting that
 * cannot be carried as sums, so budgets cannot be combined exactly.
 */
export const PER_BUDGET_METRICS: ReadonlySet<MetricKey> = new Set<MetricKey>([
  "bids",
  "impressions",
  "revenue_eur",
  "supply_cost_eur",
  "ap_conversions",
  "ap_revenue_eur",
  "ap_roas",
  "ap_roas_d7",
  "ap_xpc",
  "partner_xpc",
  "partner_xpc_d7",
  "ap_reengagements",
  "maxbid_supply_ratio",
  "maxbid_supply_impressions",
  "impression_value_eur",
  "impval_supply_ratio",
  "impval_supply_infra_ratio",
  "unique_users_impressions",
  "mse_win_rate_wins",
  "mse_win_rate_impressions",
  "selectivity",
]);

/** Budget-pool columns marking a "ROAS budget"; any above zero qualifies. */
const ROAS_GOAL_COLUMNS = ["target_roas", "secondary_target_roas"] as const;

/** Metrics restricted to ROAS budgets: without a return target, AP ROAS describes the
 * client's app rather than how the test bid. */
const ROAS_BUDGET_METRICS: ReadonlySet<MetricKey> = new Set<MetricKey>(["ap_roas", "ap_roas_d7"]);

/** Percentiles reported for a ratio distribution. 0 and 100 are the min and max. */
export const RATIO_PERCENTILES = [0, 1, 5, 50, 95, 99, 100] as const;

/** One metric's per-budget ratio distribution, summarised. */
export interface RatioStat {
  /** Weighted geometric mean of the per-budget ratios. */
  value: number;
  /** The same ratios' percentiles under the same weights, keyed by percentile. */
  percentiles: Record<number, number>;
  /** Budgets that contributed a usable ratio. */
  count: number;
}

/** Metrics sourced from the slower ClickHouse join, so a pending value reads as pending. */
export const CLICKHOUSE_METRICS: ReadonlySet<MetricKey> = new Set<MetricKey>([
  "impval_supply_ratio",
  "impval_supply_infra_ratio",
  "maxbid_supply_impressions",
  "impression_value_eur",
  "mse_win_rate_wins",
  "mse_win_rate_impressions",
  "selectivity",
]);

export interface PerBudgetRatios {
  /** Test flight id → metric → ratio summary (null when no budget qualified). */
  byFlight: Map<string, Partial<Record<MetricKey, RatioStat | null>>>;
  /** Arm-column values for rows the BigQuery report has no column for, per flight. */
  armMetrics: Map<string, Partial<Record<MetricKey, number | null>>>;
  /** Non-fatal problems, e.g. a source that failed and left some metrics blank. */
  warnings: string[];
}

/** Per-(budget, flight) sums the metrics are derived from. All money in micros. */
interface Sums {
  bids: number;
  impressions: number;
  uniqueUsers: number;
  supplyMicros: number;
  revenueMicros: number;
  /** Revenue restricted to bid days whose D1 (resp. D7) attribution window has elapsed, so each
   *  rate divides by the same days its numerator counts conversions over. */
  revenueClosedMicros: number;
  revenueClosed7Micros: number;
  adjustedBidSum: number;
  /** Won-impression sums, from ClickHouse `raw.bids` (see buildWonImpressionSQL). */
  impvalWon: number;
  adjustedBidWon: number;
  supplyWon: number;
  /** Win-rate MSE terms. For a 0/1 outcome y, Σ(y−p)² = Σp² + n_y − 2·Σ_{y=1}p, so the MSE
   *  is a ratio of sums and stays exact when budgets are added together. */
  mseBids: number;
  mseSumP2: number;
  mseNWon: number;
  mseSumPWon: number;
  mseNImp: number;
  mseSumPImp: number;
  /** Selectivity terms: exp(Σ supply·ln(1/pacing) / Σ supply) over won impressions. */
  selNum: number;
  selDen: number;
  apConversions: number;
  partnerConversions: number;
  partnerConversionsD7: number;
  apRevenueMicros: number;
  apRevenueD7Micros: number;
  apReengagements: number;
}

/** Bid-side denominators per (OLI, flight), from ClickHouse `raw.bids`. */
interface DenomRow {
  oli: string;
  flight: string;
  mse_bids: string | number | null;
  mse_sum_p2: string | number | null;
}

/** Won-side sums per (OLI, flight), from ClickHouse `raw.bids`. */
interface WonRow {
  oli: string;
  flight: string;
  impval_won: string | number | null;
  adjbid_won: string | number | null;
  supply_won_micros: string | number | null;
  mse_n_won: string | number | null;
  mse_sum_p_won: string | number | null;
  mse_n_imp: string | number | null;
  mse_sum_p_imp: string | number | null;
  sel_num: string | number | null;
  sel_den: string | number | null;
}

interface DruidRow {
  budget: string;
  flight: string;
  bids: number;
  impressions: number;
  uniq_users: number;
  adjbid_sum: number;
  supply_micros: number;
  revenue_micros: number;
  revenue_closed_micros: number;
  revenue_closed7_micros: number;
  ap_conversions: number;
  partner_conversions: number;
  partner_conversions_d7: number;
  ap_reengagements: number;
}

/** Per-(budget, flight) AP revenue, from the second Druid pass (see buildApRevenueSQL). */
interface ApRevenueRow {
  budget: string;
  flight: string;
  ap_revenue_micros: number;
  ap_revenue_d7_micros: number;
}

const emptySums = (): Sums => ({
  bids: 0,
  impressions: 0,
  uniqueUsers: 0,
  supplyMicros: 0,
  revenueMicros: 0,
  revenueClosedMicros: 0,
  revenueClosed7Micros: 0,
  adjustedBidSum: 0,
  impvalWon: 0,
  adjustedBidWon: 0,
  supplyWon: 0,
  mseBids: 0,
  mseSumP2: 0,
  mseNWon: 0,
  mseSumPWon: 0,
  mseNImp: 0,
  mseSumPImp: 0,
  selNum: 0,
  selDen: 0,
  apConversions: 0,
  partnerConversions: 0,
  partnerConversionsD7: 0,
  apRevenueMicros: 0,
  apRevenueD7Micros: 0,
  apReengagements: 0,
});

/** `YYYY-MM-DD` (UTC) shifted by whole days. */
function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split("T")[0];
}

/** `YYYY-MM-DD` → `YYYYMMDD`, the format of the `bid_ts` dimension. */
const toBidTs = (date: string): string => date.replace(/-/g, "");

function assertIntegerIds(ids: string[], label: string): void {
  for (const id of ids) {
    if (!/^\d+$/.test(id)) throw new Error(`Invalid ${label}: ${id}`);
  }
}

/**
 * Per-(budget pool, flight) aggregates over the experiment window.
 *
 * A budget pool creates one OLI per spend cycle, so the query groups on the budget pool the
 * `order_line_item_budget_pool` lookup resolves to rather than on the OLI. Grouping here is
 * what makes `uniq_users` a distinct count per budget: Druid merges the HLL sketch across the
 * rows it groups, whereas per-OLI counts could only be added up and would count a user once
 * per cycle they were active in.
 *
 * Cohorts on `bid_ts` (the bid's own day) like the arm-level query; `__time` is event time
 * and is bounded separately so Druid can prune segments. Each metric is non-zero on only one
 * `event_type`, so summing across all rows counts it once and needs no event-type filter.
 */
export function buildPerBudgetDruidSQL(flightIds: string[], startDate: string, endDate: string): string {
  assertIntegerIds(flightIds, "flight id");
  const ids = flightIds.map(id => `'${id}'`).join(",");
  const bidStart = toBidTs(startDate);
  const bidEnd = toBidTs(endDate);
  // Runs past the window so late D7 events land; Druid needs the bound to prune segments.
  const eventEnd = shiftDate(endDate, 8);
  const today = new Date().toISOString().split("T")[0];
  // Bid days whose attribution window has elapsed. Bounds the metrics whose conversions need
  // time to arrive, over numerator and denominator alike; volume metrics span the whole window.
  const closedDays = `bid_ts < '${toBidTs(shiftDate(today, -1))}'`;
  const closed7Days = `bid_ts < '${toBidTs(shiftDate(today, -7))}'`;
  const d1 = `days_since_bid <= 1 AND ${closedDays}`;
  const d7 = `days_since_bid <= 7 AND ${closed7Days}`;
  return `
    WITH b AS (
      SELECT
        LOOKUP(order_line_item, 'order_line_item_budget_pool') AS budget,
        primary_prediction_flight_id AS flight,
        bid_ts,
        TIMESTAMPDIFF(DAY, TIME_PARSE(bid_ts, 'yyyyMMdd'), FLOOR(__time TO DAY)) AS days_since_bid,
        bids, impressions, user_id, adjusted_bid, win_price_eur, buying_price_eur,
        ap_conversions, partner_conversions, ap_reengagements
      FROM bids
      WHERE __time >= TIMESTAMP '${startDate}' AND __time < TIMESTAMP '${eventEnd}'
        AND primary_prediction_flight_id IN (${ids})
        AND order_line_item <> ''
        AND LOOKUP(order_line_item, 'order_line_item_budget_pool') <> ''
        AND bid_ts >= '${bidStart}' AND bid_ts <= '${bidEnd}'
    )
    SELECT
      budget,
      flight,
      SUM(bids) AS bids,
      SUM(impressions) AS impressions,
      SUM(adjusted_bid) AS adjbid_sum,
      APPROX_COUNT_DISTINCT(user_id) FILTER (WHERE impressions = 1) AS uniq_users,
      SUM(win_price_eur) AS supply_micros,
      SUM(buying_price_eur) AS revenue_micros,
      SUM(CASE WHEN ${closedDays} THEN buying_price_eur ELSE 0 END) AS revenue_closed_micros,
      SUM(CASE WHEN ${closed7Days} THEN buying_price_eur ELSE 0 END) AS revenue_closed7_micros,
      SUM(CASE WHEN ${d1} THEN ap_conversions ELSE 0 END) AS ap_conversions,
      SUM(CASE WHEN ${d1} THEN partner_conversions ELSE 0 END) AS partner_conversions,
      SUM(CASE WHEN ${d7} THEN partner_conversions ELSE 0 END) AS partner_conversions_d7,
      SUM(CASE WHEN ${d1} THEN ap_reengagements ELSE 0 END) AS ap_reengagements
    FROM b
    GROUP BY budget, flight
  `;
}

/** Each campaign's effective conversion event: the partner event its AP revenue counts on. */
export function buildCampaignConversionEventSQL(budgetIds: string[]): string {
  assertIntegerIds(budgetIds, "budget pool id");
  return `
    SELECT CAST(ca.id AS STRING) AS campaign, LOWER(TRIM(bp.conversion_event)) AS conversion_event
    FROM \`martin-test-datalab.postgres.campaigns\` ca
    JOIN \`martin-test-datalab.postgres.budget_pools\` bp ON bp.id = ca.budget_pool_id
    WHERE ca.budget_pool_id IN (${budgetIds.join(",")})
      AND TRIM(COALESCE(bp.conversion_event, '')) <> ''
  `;
}

/**
 * `(event, campaigns)` pairs as one Druid predicate. Druid cannot join to Postgres, so the
 * campaign-to-event mapping has to be inlined into the query as literals. Campaigns are
 * grouped by their event so the clause count is the number of distinct conversion events,
 * not the number of campaigns.
 */
export function buildConversionEventPredicate(byEvent: Map<string, string[]>): string {
  const clauses: string[] = [];
  for (const [event, campaigns] of byEvent) {
    assertIntegerIds(campaigns, "campaign id");
    const ids = campaigns.map(id => `'${id}'`).join(",");
    clauses.push(`(LOWER(TRIM(partner_event)) = '${event.replace(/'/g, "''")}' AND campaign IN (${ids}))`);
  }
  return clauses.join(" OR ");
}

/**
 * AP customer revenue per (budget, flight), counting only events that are the bidding
 * campaign's own conversion event. Separate from the main pass because the predicate needs the
 * Postgres mapping, and narrow because it reads only rows that carry revenue.
 */
export function buildApRevenueSQL(flightIds: string[], startDate: string, endDate: string, predicate: string): string {
  assertIntegerIds(flightIds, "flight id");
  const ids = flightIds.join(",");
  const eventEnd = shiftDate(endDate, 8);
  const today = new Date().toISOString().split("T")[0];
  const d1 = `days_since_bid <= 1 AND bid_ts < '${toBidTs(shiftDate(today, -1))}'`;
  const d7 = `days_since_bid <= 7 AND bid_ts < '${toBidTs(shiftDate(today, -7))}'`;
  return `
    WITH r AS (
      SELECT
        LOOKUP(order_line_item, 'order_line_item_budget_pool') AS budget,
        primary_prediction_flight_id AS flight,
        bid_ts,
        TIMESTAMPDIFF(DAY, TIME_PARSE(bid_ts, 'yyyyMMdd'), FLOOR(__time TO DAY)) AS days_since_bid,
        user_id, ap_customer_revenue_eur
      FROM bids
      WHERE __time >= TIMESTAMP '${startDate}' AND __time < TIMESTAMP '${eventEnd}'
        AND primary_prediction_flight_id IN (${ids})
        AND ap_customer_revenue_eur > 0
        AND order_line_item <> ''
        AND LOOKUP(order_line_item, 'order_line_item_budget_pool') <> ''
        AND bid_ts >= '${toBidTs(startDate)}' AND bid_ts <= '${toBidTs(endDate)}'
        AND (${predicate})
    )
    SELECT
      budget,
      flight,
      SUM(CASE WHEN ${d1} THEN ap_customer_revenue_eur ELSE 0 END) AS ap_revenue_micros,
      SUM(CASE WHEN ${d7} THEN ap_customer_revenue_eur ELSE 0 END) AS ap_revenue_d7_micros
    FROM r
    GROUP BY budget, flight
  `;
}

/** Bid-side totals for the win-rate MSE denominator. No join, so this is cheap. */
export function buildBidDenominatorSQL(flightIds: string[], startDate: string, endDate: string): string {
  assertIntegerIds(flightIds, "flight id");
  const used = "bidding_metrics__cpm_regressor_predictor__reason_unused = ''";
  return `
    SELECT
      toString(order_line_item) AS oli,
      toString(primary_prediction_flight_id) AS flight,
      countIf(${used}) AS mse_bids,
      sumIf(pow(bidding_metrics__cpm_regressor_predictor__estimated_win_rate, 2), ${used}) AS mse_sum_p2
    FROM raw.bids
    WHERE base__ts >= toDateTime('${startDate} 00:00:00')
      AND base__ts < toDateTime('${shiftDate(endDate, 1)} 00:00:00')
      AND event_type = 'bid'
      AND primary_prediction_flight_id IN (${flightIds.join(",")})
    GROUP BY oli, flight
  `;
}

/**
 * Per-(OLI, flight) sums needing a bid joined to its own outcome on `id`: supply cost exists
 * only on a confirmed impression, the win-rate MSE needs each bid's prediction beside whether
 * it won, and selectivity weights `1/pacing_adjustment` (bid row) by supply cost (win row).
 *
 * A won bid produces two win rows, notification and confirmed impression, so `won` is grouped
 * per bid: `won = 1` is a won auction, `imp = 1` a confirmed impression, and only the latter
 * carries a price.
 *
 * `GLOBAL` because `raw.bids` is distributed, keyed on `cityHash64(id)` to keep the broadcast
 * small; at this scale the hash collides on single-digit rows.
 *
 * Counts only bids the predictor was used on, as `win_rate_se` does. The Σp² term comes from
 * buildBidDenominatorSQL.
 */
export function buildWonImpressionSQL(flightIds: string[], startDate: string, endDate: string): string {
  assertIntegerIds(flightIds, "flight id");
  const ids = flightIds.join(",");
  const bidWindow = `base__ts >= toDateTime('${startDate} 00:00:00') AND base__ts < toDateTime('${shiftDate(endDate, 1)} 00:00:00')`;
  // A confirmed impression follows its win by seconds to minutes, so the win side runs a day
  // longer; the join still restricts it to bids inside the window.
  const winWindow = `base__ts >= toDateTime('${startDate} 00:00:00') AND base__ts < toDateTime('${shiftDate(endDate, 2)} 00:00:00')`;
  const flightFilter = `primary_prediction_flight_id IN (${ids})`;
  const used = "b.unused = ''";
  return `
    WITH won AS (
      SELECT cityHash64(id) AS h, max(wins) AS won, max(impressions) AS imp, max(win_price_eur) AS wp
      FROM raw.bids
      WHERE ${winWindow} AND event_type = 'win' AND ${flightFilter}
      GROUP BY h
    ),
    bid AS (
      SELECT
        cityHash64(id) AS h,
        order_line_item AS oli,
        primary_prediction_flight_id AS flight,
        bidding_metrics__impression_value_predictor__imp_value AS imp_value,
        bidding_metrics__max_bid_predictor__adjusted_bid AS adjusted_bid,
        bidding_metrics__cpm_regressor_predictor__estimated_win_rate AS p,
        bidding_metrics__cpm_regressor_predictor__reason_unused AS unused,
        bidding_metrics__budget_distribution__pacing_adjustment AS pacing
      FROM raw.bids
      WHERE ${bidWindow} AND event_type = 'bid' AND ${flightFilter}
    )
    SELECT
      toString(b.oli) AS oli,
      toString(b.flight) AS flight,
      sumIf(b.imp_value, w.imp = 1) AS impval_won,
      sumIf(b.adjusted_bid, w.imp = 1) AS adjbid_won,
      sumIf(w.wp, w.imp = 1) AS supply_won_micros,
      countIf(${used} AND w.won = 1) AS mse_n_won,
      sumIf(b.p, ${used} AND w.won = 1) AS mse_sum_p_won,
      countIf(${used} AND w.imp = 1) AS mse_n_imp,
      sumIf(b.p, ${used} AND w.imp = 1) AS mse_sum_p_imp,
      sumIf(w.wp * log(1.0 / b.pacing), w.imp = 1 AND b.pacing > 0) AS sel_num,
      sumIf(w.wp, w.imp = 1 AND b.pacing > 0) AS sel_den
    FROM bid AS b
    GLOBAL INNER JOIN won AS w ON b.h = w.h
    GROUP BY oli, flight
  `;
}

/**
 * Window-unique users with an impression, per flight. Druid's `user_id` is an HLL sketch that
 * merges across rows, so this is a whole-window distinct count, not a sum of daily counts.
 */
export function buildArmUniqueUsersSQL(flightIds: string[], startDate: string, endDate: string): string {
  assertIntegerIds(flightIds, "flight id");
  const ids = flightIds.map(id => `'${id}'`).join(",");
  return `
    SELECT primary_prediction_flight_id AS flight, APPROX_COUNT_DISTINCT(user_id) AS uniq_users
    FROM bids
    WHERE __time >= TIMESTAMP '${startDate}' AND __time < TIMESTAMP '${shiftDate(endDate, 4)}'
      AND primary_prediction_flight_id IN (${ids})
      AND impressions = 1
      AND bid_ts >= '${toBidTs(startDate)}' AND bid_ts <= '${toBidTs(endDate)}'
    GROUP BY primary_prediction_flight_id
  `;
}

/** OLI → budget pool, for folding the per-OLI bid rows onto the budget pool. */
export function buildOliBudgetSQL(oliIds: string[]): string {
  assertIntegerIds(oliIds, "order line item id");
  return `
    SELECT CAST(id AS STRING) AS oli, CAST(budget_pool_id AS STRING) AS budget
    FROM \`martin-test-datalab.postgres.order_line_items\`
    WHERE id IN (${oliIds.join(",")}) AND budget_pool_id IS NOT NULL
  `;
}

/**
 * Infrastructure cost of a single bid, in EUR micros. The bidder's `BidCostEurMicros` fields
 * differ in scale — 3 in `InfrastructureCostConfig`, 3000 on the CPM scale in
 * `CPMInterpolationParams` — and both mean 3 µ€ per bid, the scale a raw bid count needs.
 */
export const BID_COST_EUR_MICROS = 3;

/**
 * Budget pools among `budgetIds` carrying at least one ROAS goal. `columns` must be the subset
 * of ROAS_GOAL_COLUMNS that `budget_pools` actually has; see buildRoasColumnSQL.
 */
export function buildRoasBudgetSQL(budgetIds: string[], columns: readonly string[]): string {
  assertIntegerIds(budgetIds, "budget pool id");
  const predicate = columns.map(c => `${c} > 0`).join(" OR ");
  return `
    SELECT CAST(id AS STRING) AS budget
    FROM \`martin-test-datalab.postgres.budget_pools\`
    WHERE id IN (${budgetIds.join(",")}) AND (${predicate})
  `;
}

/** Which of ROAS_GOAL_COLUMNS the table currently has. */
export function buildRoasColumnSQL(): string {
  const names = ROAS_GOAL_COLUMNS.map(c => `'${c}'`).join(",");
  return `
    SELECT column_name
    FROM \`martin-test-datalab.postgres.INFORMATION_SCHEMA.COLUMNS\`
    WHERE table_name = 'budget_pools' AND column_name IN (${names})
  `;
}

/** The report's arm-level formulas, applied to one budget's sums. */
function metricValue(key: MetricKey, s: Sums): number | null {
  switch (key) {
    case "bids":
      return s.bids;
    case "impressions":
      return s.impressions;
    case "unique_users_impressions":
      return s.uniqueUsers;
    case "revenue_eur":
      return s.revenueMicros / 1e6;
    case "supply_cost_eur":
      return s.supplyMicros / 1e6;
    case "ap_conversions":
      return s.apConversions;
    case "ap_revenue_eur":
      return s.apRevenueMicros / 1e6;
    // Each rate divides by revenue over the same bid days its numerator counts, so a window
    // whose cohort has not elapsed reads as no data rather than as a zero rate.
    case "ap_roas":
      return s.revenueClosedMicros ? s.apRevenueMicros / s.revenueClosedMicros : null;
    case "ap_roas_d7":
      return s.revenueClosed7Micros ? s.apRevenueD7Micros / s.revenueClosed7Micros : null;
    case "ap_xpc":
      return s.revenueClosedMicros ? (s.apConversions * 1e6) / s.revenueClosedMicros : null;
    case "partner_xpc_d7":
      return s.revenueClosed7Micros ? (s.partnerConversionsD7 * 1e6) / s.revenueClosed7Micros : null;
    case "partner_xpc":
      return s.revenueClosedMicros ? (s.partnerConversions * 1e6) / s.revenueClosedMicros : null;
    case "ap_reengagements":
      return s.apReengagements;
    // Supply-weighted geometric mean of 1/pacing_adjustment, as in raw_metrics.
    case "selectivity":
      return s.selDen ? Math.exp(s.selNum / s.selDen) : null;
    case "mse_win_rate_wins":
      return s.mseBids ? (s.mseSumP2 + s.mseNWon - 2 * s.mseSumPWon) / s.mseBids : null;
    case "mse_win_rate_impressions":
      return s.mseBids ? (s.mseSumP2 + s.mseNImp - 2 * s.mseSumPImp) / s.mseBids : null;
    // imp_value is a micro-CPM: /1e3 for one impression, /1e6 for euro.
    case "impression_value_eur":
      return s.impvalWon / 1e9;
    // Mean max bid over all bids, scaled to the impressions it is compared against.
    case "maxbid_supply_ratio":
      return s.bids && s.supplyMicros && s.impressions
        ? (s.adjustedBidSum / s.bids) * (s.impressions / (s.supplyMicros * 1000))
        : null;
    // Both averaged over won impressions, so the impression count cancels.
    case "maxbid_supply_impressions":
      return s.supplyWon ? s.adjustedBidWon / (s.supplyWon * 1000) : null;
    // A micro-CPM over a micro-EUR total, hence ×1000.
    case "impval_supply_ratio":
      return s.supplyWon ? s.impvalWon / (s.supplyWon * 1000) : null;
    // Every bid costs BID_COST_EUR_MICROS whether or not it won; only impressions cost supply.
    case "impval_supply_infra_ratio": {
      const cost = s.supplyWon + s.bids * BID_COST_EUR_MICROS;
      return cost ? s.impvalWon / (cost * 1000) : null;
    }
    default:
      return null;
  }
}

/** Weighted quantile of ratios already sorted ascending, by cumulative weight. */
function weightedQuantile(sorted: { ratio: number; weight: number }[], totalWeight: number, q: number): number {
  const target = totalWeight * q;
  let cumulative = 0;
  for (const s of sorted) {
    cumulative += s.weight;
    if (cumulative >= target) return s.ratio;
  }
  return sorted[sorted.length - 1].ratio;
}

/**
 * `exp(Σ w·ln(r) / Σ w)` plus the weighted spread of the same ratios. Non-positive weights
 * and ratios are dropped, so one zero-metric budget cannot take the aggregate to 0 or NaN.
 */
export function weightedGeometricMean(samples: { ratio: number; weight: number }[]): RatioStat | null {
  const usable = samples.filter(s => s.weight > 0 && s.ratio > 0 && Number.isFinite(s.ratio));
  if (usable.length === 0) return null;
  let numerator = 0;
  let totalWeight = 0;
  for (const { ratio, weight } of usable) {
    numerator += Math.log(ratio) * weight;
    totalWeight += weight;
  }
  if (!(totalWeight > 0)) return null;
  const sorted = [...usable].sort((a, b) => a.ratio - b.ratio);
  const percentiles: Record<number, number> = {};
  for (const p of RATIO_PERCENTILES) percentiles[p] = weightedQuantile(sorted, totalWeight, p / 100);
  return {
    value: Math.exp(numerator / totalWeight),
    percentiles,
    count: usable.length,
  };
}

const toNum = (v: unknown): number => (v == null ? 0 : Number(v));

/**
 * Per-budget ratio per test flight, versus the control arm. `onPartial` receives the Druid
 * metrics as soon as they land; the resolved value adds the slower ClickHouse ones.
 */
export async function fetchPerBudgetRatios(
  flights: Flight[],
  startDate: string,
  endDate: string,
  onPartial?: (partial: PerBudgetRatios) => void,
  signal?: AbortSignal,
): Promise<PerBudgetRatios> {
  const empty: PerBudgetRatios = {
    byFlight: new Map(),
    armMetrics: new Map(),
    warnings: [],
  };
  // Falls back to the first flight when no arm is flagged as control, matching how the
  // report picks its baseline for the Δ-vs-control columns.
  const control = flights.find(f => f.is_control) ?? flights[0];
  const testFlights = flights.filter(f => f.flight_id !== control?.flight_id);
  if (!control || testFlights.length === 0) return empty;

  const flightIds = flights.map(f => f.flight_id);
  // A ClickHouse failure blanks only the metrics it feeds.
  const druidPromise = runDruidQuery(
    buildPerBudgetDruidSQL(flightIds, startDate, endDate),
    signal,
  ) as Promise<unknown> as Promise<DruidRow[]>;
  // Denominators need no join, so they land in seconds; failure only blanks the MSE rows.
  const denomPromise = (
    runClickHouseQuery(buildBidDenominatorSQL(flightIds, startDate, endDate), signal) as Promise<unknown> as Promise<
      DenomRow[]
    >
  ).then(
    rows => ({ rows, error: null as string | null }),
    (err: unknown) => ({ rows: [] as DenomRow[], error: String(err) }),
  );
  const impValuePromise = (
    runClickHouseQuery(buildWonImpressionSQL(flightIds, startDate, endDate), signal) as Promise<unknown> as Promise<
      WonRow[]
    >
  ).then(
    rows => ({ rows, error: null as string | null }),
    (err: unknown) => ({ rows: [] as WonRow[], error: String(err) }),
  );

  const armUsersPromise = (
    runDruidQuery(buildArmUniqueUsersSQL(flightIds, startDate, endDate), signal) as Promise<unknown> as Promise<
      { flight: string; uniq_users: number }[]
    >
  ).then(
    r => r,
    () => [] as { flight: string; uniq_users: number }[],
  );

  const rows = await druidPromise;
  const uniqueUsersByFlight = new Map<string, number>();
  for (const r of await armUsersPromise) uniqueUsersByFlight.set(String(r.flight), toNum(r.uniq_users));

  /**
   * Arm-level values for rows the BigQuery report has no column for. Ratios of sums are the
   * per-budget sums added up; unique users are not additive, so they have their own aggregate.
   */
  const armMetrics = (): Map<string, Partial<Record<MetricKey, number | null>>> => {
    const perFlight = new Map<string, Partial<Record<MetricKey, number | null>>>();
    const totals = new Map<string, Sums>();
    for (const armsByBudget of sums.values()) {
      for (const [flight, s] of armsByBudget) {
        const t = totals.get(flight) ?? emptySums();
        t.adjustedBidWon += s.adjustedBidWon;
        t.supplyWon += s.supplyWon;
        t.impvalWon += s.impvalWon;
        t.partnerConversions += s.partnerConversions;
        t.partnerConversionsD7 += s.partnerConversionsD7;
        t.revenueMicros += s.revenueMicros;
        t.revenueClosedMicros += s.revenueClosedMicros;
        t.revenueClosed7Micros += s.revenueClosed7Micros;
        t.mseBids += s.mseBids;
        t.mseSumP2 += s.mseSumP2;
        t.mseNWon += s.mseNWon;
        t.mseSumPWon += s.mseSumPWon;
        t.mseNImp += s.mseNImp;
        t.mseSumPImp += s.mseSumPImp;
        totals.set(flight, t);
      }
    }
    for (const flight of new Set([...totals.keys(), ...uniqueUsersByFlight.keys()])) {
      const t = totals.get(flight);
      perFlight.set(flight, {
        maxbid_supply_impressions: t ? metricValue("maxbid_supply_impressions", t) : null,
        impression_value_eur: t ? metricValue("impression_value_eur", t) : null,
        partner_xpc: t ? metricValue("partner_xpc", t) : null,
        partner_xpc_d7: t ? metricValue("partner_xpc_d7", t) : null,
        mse_win_rate_wins: t ? metricValue("mse_win_rate_wins", t) : null,
        mse_win_rate_impressions: t ? metricValue("mse_win_rate_impressions", t) : null,
        unique_users_impressions: uniqueUsersByFlight.get(flight) ?? null,
      });
    }
    return perFlight;
  };
  const warnings: string[] = [];
  if (rows.length === 0) return empty;

  const sums = new Map<string, Map<string, Sums>>(); // budget → flight → sums
  for (const row of rows) {
    const budget = String(row.budget);
    if (!budget) continue;
    let byFlight = sums.get(budget);
    if (!byFlight) {
      byFlight = new Map();
      sums.set(budget, byFlight);
    }
    let s = byFlight.get(String(row.flight));
    if (!s) {
      s = emptySums();
      byFlight.set(String(row.flight), s);
    }
    s.bids += toNum(row.bids);
    s.impressions += toNum(row.impressions);
    // Assigned, not accumulated: the query returns one row per (budget, flight) and a distinct
    // count of users cannot be added up across rows.
    s.uniqueUsers = toNum(row.uniq_users);
    s.adjustedBidSum += toNum(row.adjbid_sum);
    s.supplyMicros += toNum(row.supply_micros);
    s.revenueMicros += toNum(row.revenue_micros);
    s.revenueClosedMicros += toNum(row.revenue_closed_micros);
    s.revenueClosed7Micros += toNum(row.revenue_closed7_micros);
    s.apConversions += toNum(row.ap_conversions);
    s.partnerConversions += toNum(row.partner_conversions);
    s.partnerConversionsD7 += toNum(row.partner_conversions_d7);
    s.apReengagements += toNum(row.ap_reengagements);
  }

  /**
   * AP customer revenue, counted only on each campaign's budget conversion event. The mapping is
   * Postgres-side, so this is a BigQuery lookup feeding a second Druid pass; on failure the
   * sums stay zero and every AP revenue row reports nothing rather than a wrong number.
   */
  try {
    const budgetIds = [...sums.keys()].filter(id => /^\d+$/.test(id));
    const events = (await runBigQueryQuery(buildCampaignConversionEventSQL(budgetIds))) as unknown as {
      campaign: string;
      conversion_event: string;
    }[];
    const byEvent = new Map<string, string[]>();
    for (const e of events) {
      const event = String(e.conversion_event);
      const campaign = String(e.campaign);
      if (!event || !/^\d+$/.test(campaign)) continue;
      const list = byEvent.get(event);
      if (list) list.push(campaign);
      else byEvent.set(event, [campaign]);
    }
    if (byEvent.size > 0) {
      const apRows = (await runDruidQuery(
        buildApRevenueSQL(flightIds, startDate, endDate, buildConversionEventPredicate(byEvent)),
        signal,
      )) as unknown as ApRevenueRow[];
      for (const row of apRows) {
        const s = sums.get(String(row.budget))?.get(String(row.flight));
        if (!s) continue;
        s.apRevenueMicros += toNum(row.ap_revenue_micros);
        s.apRevenueD7Micros += toNum(row.ap_revenue_d7_micros);
      }
    }
  } catch (err: unknown) {
    warnings.push(`AP revenue rows unavailable (conversion-event lookup): ${String(err)}`);
  }

  /**
   * Budgets with a ROAS goal, for the rows in ROAS_BUDGET_METRICS. Left null if the lookup
   * fails: those rows then report nothing rather than silently reverting to every budget.
   */
  let roasBudgets: Set<string> | null = null;
  try {
    const budgetIds = [...sums.keys()].filter(id => /^\d+$/.test(id));
    if (budgetIds.length > 0) {
      const cols = (await runBigQueryQuery(buildRoasColumnSQL())) as unknown as { column_name: string }[];
      const present = cols.map(c => String(c.column_name));
      if (present.length === 0) throw new Error(`none of ${ROAS_GOAL_COLUMNS.join(", ")} exist on budget_pools`);
      const roas = (await runBigQueryQuery(buildRoasBudgetSQL(budgetIds, present))) as unknown as {
        budget: string;
      }[];
      roasBudgets = new Set(roas.map(r => String(r.budget)));
    } else {
      roasBudgets = new Set();
    }
  } catch (err: unknown) {
    warnings.push(`AP ROAS rows unavailable (ROAS budget lookup): ${String(err)}`);
  }

  /** Aggregate whatever is in `sums` right now, so it can run once per phase. */
  const summarise = (): PerBudgetRatios => {
    const byFlight = new Map<string, Partial<Record<MetricKey, RatioStat | null>>>();
    for (const arm of testFlights) {
      const metrics: Partial<Record<MetricKey, RatioStat | null>> = {};
      for (const key of PER_BUDGET_METRICS) {
        const roasOnly = ROAS_BUDGET_METRICS.has(key);
        const samples: { ratio: number; weight: number }[] = [];
        for (const [budget, armsByBudget] of sums) {
          if (roasOnly && !roasBudgets?.has(budget)) continue;
          const c = armsByBudget.get(control.flight_id);
          const t = armsByBudget.get(arm.flight_id);
          if (!c || !t) continue;
          const cv = metricValue(key, c);
          const tv = metricValue(key, t);
          if (cv == null || tv == null || !(cv > 0)) continue;
          // Identical across arms, so a test arm's own spend cannot reweight its budgets.
          samples.push({ ratio: tv / cv, weight: c.revenueMicros });
        }
        metrics[key] = weightedGeometricMean(samples);
      }
      byFlight.set(arm.flight_id, metrics);
    }
    return {
      byFlight,
      armMetrics: armMetrics(),
      warnings,
    };
  };

  onPartial?.(summarise());

  const denom = await denomPromise;
  if (denom.error) warnings.push(`Win-rate MSE unavailable (ClickHouse): ${denom.error}`);
  const impValue = await impValuePromise;
  if (impValue.error) warnings.push(`Won-impression metrics unavailable (ClickHouse): ${impValue.error}`);

  // The bid-level source reports per OLI, so its rows are folded onto the budget pool the Druid
  // rows are already grouped by. Both sides resolve `order_line_items.budget_pool_id`.
  const oliIds = [...new Set([...denom.rows, ...impValue.rows].map(r => String(r.oli)))].filter(id => /^\d+$/.test(id));
  const budgetByOli = new Map<string, string>();
  if (oliIds.length > 0) {
    try {
      const mapping = (await runBigQueryQuery(buildOliBudgetSQL(oliIds))) as unknown as {
        oli: string;
        budget: string;
      }[];
      for (const m of mapping) budgetByOli.set(String(m.oli), String(m.budget));
    } catch (err: unknown) {
      warnings.push(`Bid-level metrics unavailable (budget lookup): ${String(err)}`);
    }
  }

  for (const row of denom.rows) {
    const budget = budgetByOli.get(String(row.oli));
    const s = budget ? sums.get(budget)?.get(String(row.flight)) : undefined;
    if (!s) continue;
    s.mseBids += toNum(row.mse_bids);
    s.mseSumP2 += toNum(row.mse_sum_p2);
  }

  for (const row of impValue.rows) {
    const budget = budgetByOli.get(String(row.oli));
    if (!budget) continue;
    // Needs the Druid row for the revenue weight.
    const s = sums.get(budget)?.get(String(row.flight));
    if (!s) continue;
    s.impvalWon += toNum(row.impval_won);
    s.adjustedBidWon += toNum(row.adjbid_won);
    s.supplyWon += toNum(row.supply_won_micros);
    s.mseNWon += toNum(row.mse_n_won);
    s.mseSumPWon += toNum(row.mse_sum_p_won);
    s.mseNImp += toNum(row.mse_n_imp);
    s.mseSumPImp += toNum(row.mse_sum_p_imp);
    s.selNum += toNum(row.sel_num);
    s.selDen += toNum(row.sel_den);
  }

  return summarise();
}
